import { useEffect, useMemo } from "react";

// Every remembered value lives in this one localStorage entry, capped at
// MAX_ENTRIES and dropping the least recently used, because keys are often
// per entity (a recruitment's users) and the Auth0 session shares the quota.
const STORAGE_KEY = "lastKnownSizes";
const MAX_ENTRIES = 1000;

let entries: Record<string, unknown> | undefined;
let writeScheduled = false;

function load() {
  if (!entries) {
    try {
      const parsed: unknown = JSON.parse(
        localStorage.getItem(STORAGE_KEY) ?? "{}",
      );
      entries =
        parsed && typeof parsed === "object"
          ? (parsed as Record<string, unknown>)
          : {};
    } catch {
      entries = {};
    }
  }
  return entries;
}

// Tables are named after their GraphQL query when they have no id, so a key
// can hold a whole query. Long keys are shortened to a prefix and a hash.
function shorten(key: string) {
  if (key.length <= 120) {
    return key;
  }
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
  }
  return `${key.slice(0, 60)}#${(hash >>> 0).toString(36)}`;
}

/** The value last remembered under `key`, if any. */
export function recall<T>(key: string) {
  return load()[shorten(key)] as T | undefined;
}

/** Remembers `value` under `key` for the next page load. */
export function remember(fullKey: string, value: unknown) {
  const key = shorten(fullKey);
  const all = load();
  // Re-insert so the key order runs from least to most recently used.
  delete all[key];
  all[key] = value;
  const keys = Object.keys(all);
  for (const old of keys.slice(0, Math.max(keys.length - MAX_ENTRIES, 0))) {
    delete all[old];
  }
  // One write for everything a page load remembers.
  if (!writeScheduled) {
    writeScheduled = true;
    setTimeout(() => {
      writeScheduled = false;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(load()));
      } catch {
        // Storage full or blocked: the next load guesses instead.
      }
    }, 1000);
  }
}

/**
 * `value` once it has loaded, and before that the value it had the last time
 * this browser saw it, so a skeleton can take the size the content will have
 * (how many rows, whether a section shows).
 */
export function useLastKnown<T>(
  key: string,
  value: T | undefined,
  fallback: T,
): T {
  const loading = value === undefined;
  // Read again each time loading starts, to get what the last load stored.
  const stored = useMemo(
    () => (loading ? recall<T>(key) : undefined),
    [key, loading],
  );

  useEffect(() => {
    if (value !== undefined) {
      remember(key, value);
    }
  }, [key, value]);

  return value ?? stored ?? fallback;
}

/**
 * `useLastKnown` for one entity of a kind: what this entity had last time,
 * and the first time what the last entity of the kind had.
 */
export function useLastKnownFor<T>(
  kind: string,
  id: string,
  value: T | undefined,
  fallback: T,
): T {
  const typical = useLastKnown(kind, value, fallback);
  return useLastKnown(`${kind}_${id}`, value, typical);
}
